/**
 * Held actions (docs/rbac.md section 5, "Humans approve"; step 6): an agent's high-impact request does not run. AIOE's
 * own (the workbench reads the answer; it mirrors none of this file). Only with AIOE_RBAC on.
 *
 * The flow, for a machine calling on an agent's behalf (the actor claim, shared/rbac/agentPrincipal.ts):
 *
 *   1. The request is high-impact by the organisation's effective list (shared/rbac/highImpact.ts `effectiveHighImpact`
 *      of the policy's `agents.highImpact`). AIOE answers **202** with {@link HeldAnswer} (`code: "held_for_approval"`)
 *      and nothing runs. The same request sent again while it waits answers the same held action (no second Inbox record).
 *   2. A person with Act on the resource approves or rejects it: the Inbox (`aioe.held-action.decide`, a sign-in only,
 *      recent when roles are on), or Settings › Roles and permissions. Audited `action.held`, `action.approved`,
 *      `action.rejected`.
 *   3. The machine reads `GET /me/v1/held-actions/:id` (its own device token) until it is decided, then, once approved,
 *      sends **the same request again** with `x-aioe-held-action: <id>`. AIOE checks it is the same request (method, path,
 *      query and body, the same machine, agent and person: its fingerprint), takes the approval once (`used`, audited
 *      `action.released`) and lets the request run as any other: the approval never widens what the agent and its person
 *      may do. The approval lasts {@link HELD_ACTION_APPROVAL_HOURS} hours; an action waits at most
 *      {@link HELD_ACTION_PENDING_DAYS} days.
 *
 * AIOE never replays a request itself: the machine that asked holds the request, its proof of possession and its context,
 * so it alone sends it again, and AIOE only lets an approved one through once.
 *
 *   GET  /me/v1/held-actions/:id                      the machine that asked: { action }
 *   GET  /admin/v1/rbac/held-actions?state=           Read on Roles: the organisation's held actions, newest first
 *   POST /admin/v1/rbac/held-actions/:id/decide       { decision: "approve" | "reject", reason? } a person with Act on it
 */
import { z } from "zod";
export const HELD_ACTION_SCHEMA = "aioe.held-action/v1";
/** The 202's code. */
export const HELD_FOR_APPROVAL = "held_for_approval";
/** The header a machine sends an approved held action's id in, on the same request again. */
export const HELD_ACTION_HEADER = "x-aioe-held-action";
/** The Inbox's platform action that decides one. */
export const HELD_ACTION_DECIDE_RPC = "aioe.held-action.decide";
/** How long an approval may be used, from when it was given. */
export const HELD_ACTION_APPROVAL_HOURS = 24;
/** How long a held action waits for a person before it lapses. */
export const HELD_ACTION_PENDING_DAYS = 7;
export const HELD_ACTION_STATES = ["pending", "approved", "rejected", "used", "expired"];
export const heldActionStateSchema = z.enum(HELD_ACTION_STATES);
/**
 * The refusals of the actor claim and of a held action, by code (each answered with `{ error, message }`):
 *   actor_not_on_this_machine 403  the claim names an agent of a team that neither runs on nor is assigned to this machine
 *   actor_unknown             403  the claim names no agent of this organisation
 *   actor_team_mismatch       400  `x-aioe-actor-team` is not the named agent's team
 *   agent_role                403  the person may, the agent may not (its own role, or the Member ceiling)
 *   held_action_rejected      403  a person rejected it: do not send it again
 *   held_action_used          409  its approval was already used: it ran once
 *   held_action_expired       409  it lapsed before anyone decided, or its approval was not used in time: ask again
 *   held_action_mismatch      409  the request sent with the header is not the one that was held, or not from its machine
 *   held_action_not_found     404  no such held action for this machine
 */
export const HELD_ACTION_ERRORS = {
    actor_not_on_this_machine: "This machine may speak only for agents of teams that run on it or are assigned to it.",
    actor_unknown: "No agent of this organisation goes by that name.",
    actor_team_mismatch: "The agent's team is not the one this request names.",
    held_action_rejected: "A person rejected this action, so it will not run. Do not send it again.",
    held_action_used: "This approval was already used: the action ran once and does not run again.",
    held_action_expired: "This held action lapsed. Ask again if it is still needed.",
    held_action_mismatch: "This is not the request that was held, or not from the machine that asked.",
    held_action_not_found: "There is no such held action for this machine.",
};
/** The sentence on a request whose approval was used and which its own handler then refused (the approval is spent). */
export function approvalUsedButRefused(status, why) {
    const reason = why?.trim() ? why.trim().replace(/[.\s]+$/, "") : `it answered ${status}`;
    return `Your approval was used, but the action itself was refused: ${reason}. Nothing ran, and the approval is spent, so ask for a new one if it is still needed.`;
}
/** One held action, as the console, the machine and the Inbox read it. */
export const heldActionSchema = z.object({
    id: z.string(),
    state: heldActionStateSchema,
    /** The entry of the effective high-impact list that held it (a default kind, a verb, or `<area>:<verb>`). */
    kind: z.string(),
    /** Why, in words that follow "it": "sends an order to a machine". */
    words: z.string(),
    method: z.string(),
    /** The path it was sent to, without its query. */
    path: z.string(),
    area: z.string().optional(),
    verb: z.string(),
    agent: z.object({ principal: z.string(), kind: z.enum(["member", "specialist", "unnamed"]), name: z.string().optional(), teamId: z.string().optional() }),
    /** The person it acts for; absent on a trigger. */
    person: z.object({ id: z.string(), name: z.string().optional() }).optional(),
    /** On a trigger: the workflow run's owner. */
    accountable: z.object({ id: z.string(), name: z.string().optional() }).optional(),
    deviceId: z.string(),
    projectId: z.string().optional(),
    teamId: z.string().optional(),
    run: z.string().optional(),
    session: z.string().optional(),
    createdAt: z.string(),
    /** Pending: when it lapses. Approved: until when the approval may be used. */
    expiresAt: z.string(),
    decidedAt: z.string().optional(),
    decidedBy: z.object({ id: z.string(), name: z.string().optional() }).optional(),
    reason: z.string().optional(),
    usedAt: z.string().optional(),
    /** The approval was used, and the action's own handler then refused it: why, so a person sees it was not done. */
    refused: z.object({ at: z.string(), status: z.number(), error: z.string().optional(), message: z.string().optional() }).optional(),
});
/** The 202 a held request is answered with. */
export const heldAnswerSchema = z.object({
    schema: z.literal(HELD_ACTION_SCHEMA),
    code: z.literal(HELD_FOR_APPROVAL),
    heldActionId: z.string(),
    state: heldActionStateSchema,
    kind: z.string(),
    words: z.string(),
    message: z.string(),
    /** Send the same request again with this header once `GET statusPath` says `approved`. */
    retryWith: z.object({ header: z.literal(HELD_ACTION_HEADER), value: z.string() }),
    statusPath: z.string(),
    expiresAt: z.string(),
});
export const heldActionDecisionSchema = z.object({ decision: z.enum(["approve", "reject"]), reason: z.string().trim().max(2000).optional() });
export const heldActionListSchema = z.object({ schema: z.literal(HELD_ACTION_SCHEMA), actions: z.array(heldActionSchema), waiting: z.number(), /** Approved, used, then refused by the action's own handler (the last week). */ refused: z.array(heldActionSchema).optional() });
/** A held action in a line, for the Inbox and the screen: "Rey (Payments) wants to send an order to a machine". */
export function heldActionTitle(h) {
    const who = h.agent.name?.trim() || "An agent";
    return `${who} wants to do something that waits for a person: it ${h.words}`.slice(0, 300);
}
//# sourceMappingURL=heldActions.js.map